import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  Button,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  makeStyles,
  useTheme,
} from "@material-ui/core";

import { CellReceptionDisplay } from "../../components/Icons/CellReceptionDisplay";
import { StringUtils } from "../../util";

const useStyles = makeStyles((theme) => ({
  table: {
    background: theme.palette.background.paper,
  },
  tableCell: {
    borderColor: theme.palette.primary.divider,
  },
  tableHeadCell: {
    borderColor: theme.palette.primary.divider,
    padding: theme.spacing(2),
  },
  iconMargin: {
    marginRight: theme.spacing(1),
  },
  center: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  typography: {
    color: theme.palette.text.primary,
  },
}));

const FleetTable = ({
  vehiclesList,
  setSortBy,
  setOrderBy,
  fleetId,
  activeFleet,
  orderBy,
  sortBy,
}) => {
  const DEFAULT_TABLE_ROWS = 50;
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowPerPage] = useState(DEFAULT_TABLE_ROWS);
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const columnKeys = [
    "shortcodes",
    "target_state",
    "last_reported_state",
    "battery_charge",
    "last_ingest",
    "last_reported_network",
    "cell_bars",
  ];

  return (
    <Table className={classes.table} size="small" data-testid="fleet-table">
      <TableHead>
        <TableRow>
          {columnKeys.map((key) => (
            <TableCell className={classes.tableHeadCell} align="center" key={key}>
              <TableSortLabel
                active={sortBy === key}
                direction={orderBy === "desc" ? "desc" : "asc"}
                onClick={() =>
                  sortBy === key ? setOrderBy(orderBy === "desc" ? "asc" : "desc") : setSortBy(key)
                }
              >
                <Typography className={classes.typography}>{t(`Fleet.Table.${key}`)}</Typography>
              </TableSortLabel>
            </TableCell>
          ))}
          <TableCell className={classes.tableHeadCell} align="center"></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {vehiclesList.slice(rowsPerPage * pageNumber, rowsPerPage * (pageNumber + 1)).map((v) => (
          <TableRow key={v.id}>
            <TableCell className={classes.tableCell} component="th" align="center">
              {v.shortcodes && v.shortcodes[0] ? v.shortcodes[0].shortcode : null}
            </TableCell>
            <TableCell className={classes.tableCell} align="center">
              <div className={classes.center}>
                <SvgIcon
                  fontSize="small"
                  className={classes.iconMargin}
                  htmlColor={
                    theme.palette.statusColors[v.has_state_mismatch ? "mismatched" : v.target_state]
                  }
                >
                  <circle cx="12" cy="12" r="12" />
                </SvgIcon>
                {t(`Vehicles.Detail.Statuses.${v.target_state}`)}
              </div>
            </TableCell>
            <TableCell className={classes.tableCell} align="center">
              {v.last_reported_state ? t(`Vehicles.Detail.Statuses.${v.last_reported_state}`) : ""}
            </TableCell>
            <TableCell className={classes.tableCell} align="center">
              {v.battery_charge}%
            </TableCell>
            <TableCell className={classes.tableCell} align="center">
              {v.last_ingest &&
                `${StringUtils.convertTz(
                  v.last_ingest,
                  "UTC",
                  activeFleet.timezone,
                  "DATETIME_FULL",
                )}`}
            </TableCell>
            <TableCell className={classes.tableCell} align="center">
              {v.last_reported_network || ""}
            </TableCell>
            <TableCell className={classes.tableCell} style={{}}>
              <CellReceptionDisplay percentage={v.cell_bars * 25} width={24} height={16} />
            </TableCell>
            <TableCell className={classes.tableCell} align="center">
              <Link to={`/fleets/${fleetId}/vehicles/${v.id}`}>
                <Button>{t("Fleet.Table.View")}</Button>
              </Link>
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TablePagination
            rowsPerPageOptions={[5, 10, 50, 100]}
            count={vehiclesList.length}
            rowsPerPage={rowsPerPage}
            page={pageNumber}
            backIconButtonProps={{
              "aria-label": "Previous Page",
            }}
            nextIconButtonProps={{
              "aria-label": "Next Page",
            }}
            onChangePage={(e, page) => setPageNumber(page)}
            onChangeRowsPerPage={(e) => setRowPerPage(e.target.value)}
            className={classes.tableCell}
            style={{ border: "none" }}
          />
        </TableRow>
      </TableBody>
    </Table>
  );
};

FleetTable.propTypes = {
  vehiclesList: PropTypes.array.isRequired,
  orderBy: PropTypes.string.isRequired,
  sortBy: PropTypes.string.isRequired,
  setOrderBy: PropTypes.func.isRequired,
  setSortBy: PropTypes.func.isRequired,
  fleetId: PropTypes.string.isRequired,
  activeFleet: PropTypes.object.isRequired,
};

FleetTable.defaultProps = {};

export default FleetTable;
